import { get, useIntervalFn } from '@vueuse/core';
import type { MaybeRef } from 'vue';
import { computed, ref, watch } from 'vue';
import type { IBetBuilderRecommendation } from '@/interface/IBetBuilderRecommendation';
import type { ISingleMatchBetBuilderCombinationDisplayRule } from '@/interface/betBuilder';
import { RecommendationApi } from '@/libraries/recommendation/recommendationApi';

const liveRecommendationsPollIntervalMs = 5_000;
const nonLiveRecommendationsPollIntervalMs = 15_000;

export function useBetBuilderRecommendationsByMatch(
    matchId: number,
    isLive: MaybeRef<boolean | undefined>,
    displayRule: MaybeRef<ISingleMatchBetBuilderCombinationDisplayRule>,
    isEnabled: MaybeRef<boolean> = true,
) {
    const betBuilderRecommendations = ref<IBetBuilderRecommendation[]>([]);
    const isLoaded = ref(false);

    const pollIntervalMs = computed(() => (get(isLive) ? liveRecommendationsPollIntervalMs : nonLiveRecommendationsPollIntervalMs));

    async function fetchRecommendations() {
        if (!get(isEnabled) || !matchId) {
            betBuilderRecommendations.value = [];
            isLoaded.value = true;
            return;
        }

        try {
            betBuilderRecommendations.value = (await RecommendationApi.getBetBuilderRecommendationsByMatch(matchId, get(displayRule)));
        } catch (error) {
            console.error('Failed to fetch bet builder recommendations by match', error);
        } finally {
            isLoaded.value = true;
        }
    }

    useIntervalFn(() => {
        if (get(isEnabled)) {
            fetchRecommendations();
        }
    }, pollIntervalMs);

    watch(
        () => get(isEnabled),
        () => {
            fetchRecommendations();
        },
    );

    fetchRecommendations();

    return {
        betBuilderRecommendations,
        isLoaded,
    };
}
